/**
 * Core helpers shared by resolution, instant actions, previews, views and bots: lookups, costs, the
 * economy formulas, and the functions that change indices and Public Support with every clamp and
 * ideology rule applied in one place.
 */
import type {
  CountryId,
  CountryState,
  CountryStats,
  GameEvent,
  HeatStage,
  IndexKey,
  ProgramId,
  Treaty,
  TreatyType,
  TurnNotes,
  UnitType,
  WorldState,
} from '../shared/types.ts';
import type { NoticeKind } from '../shared/protocol.ts';
import { IDEOLOGIES, type IdeologyData } from './data/ideologies.ts';
import { SLOTS, type SlotData, type StageRow } from './data/slots.ts';
import {
  CLIMATE_NUMBERS,
  FLOWS,
  HEAT,
  LIMITS,
  PROGRAM_UPKEEP_PERCENT,
  PROGRAMS,
  ROYAL_GRANTS,
  SCORING,
  UNIT_COST,
  UNIT_UPKEEP_PERCENT,
  UNIVERSAL,
} from './data/rules.ts';
import { CLASSIC_PARS, PARS_BY_PAIRING } from './data/world.ts';
import { Rng } from './rng.ts';
import {
  ensureAgendas,
  forestCarbonLine,
  freeTransit,
  heatSafeCost,
  irrigationFood,
  laneIncome,
  oilBridge,
  pactDiscount,
  passed,
  permafrostUpkeep,
  programCostHook,
  tippingLowPrice,
  tradeHubCap,
} from './agenda-hooks.ts';
import { clamp, getCountry, nationalIndex } from './util.ts';

/** A notice for one nation's screens (the Worker maps it to a sound and a toast). */
export interface EngineNotice {
  country: CountryId;
  kind: NoticeKind;
  text: string;
}

/** A template chat line from an AI nation, to post in the pair channel with `to`. */
export interface AiMessage {
  from: CountryId;
  to: CountryId;
  text: string;
}

/** Mutable working context used while applying changes to a cloned state. */
export interface Ctx {
  s: WorldState;
  rng: Rng;
  events: GameEvent[];
  t: number;
  notices: EngineNotice[];
  messages: AiMessage[];
}

export function makeCtx(s: WorldState): Ctx {
  ensureAgendas(s);
  return { s, rng: new Rng(s.rng), events: [], t: s.turn, notices: [], messages: [] };
}

export function notify(ctx: Ctx, country: CountryId, kind: NoticeKind, text: string): void {
  const c = ctx.s.countries[country];
  if (c && !c.isAI) ctx.notices.push({ country, kind, text });
}

/** Writes the RNG state back into the world. Call before returning a context's state. */
export function finishCtx(ctx: Ctx): void {
  ctx.s.rng = ctx.rng.state;
}

export function ev(ctx: Ctx, e: Omit<GameEvent, 't'>): void {
  ctx.events.push({ t: ctx.t, ...e });
}

export const ideo = (c: CountryState | CountryId): IdeologyData => IDEOLOGIES[typeof c === 'string' ? c : c.id];
export const slotOf = (c: CountryState): SlotData => SLOTS[c.slot];
export const shortName = (id: CountryId): string => IDEOLOGIES[id].shortName;

export function nations(s: WorldState): CountryState[] {
  const out: CountryState[] = [];
  for (const id of s.nations) {
    const c = s.countries[id];
    if (c) out.push(c);
  }
  return out;
}

export function emptyNotes(): TurnNotes {
  return {
    trades: 0, aidGiven: 0, greenCut: 0, innovationAct: false, counterintel: false, leverageWin: false,
    humanRights: false, treatiesSigned: 0, transparencyClauses: 0, refugeesWelcomed: 0, liftedStandingTariff: false,
    pledged: false, alliancesJoined: 0, programsBuilt: 0, diversified: false, attacked: false, wasAttacked: false,
  };
}

export function emptyStats(): CountryStats {
  return {
    tradesDone: 0, tariffsImposed: 0, embargoesImposed: 0, warsDeclared: 0, aggressions: 0, attacksMade: 0,
    zonesLost: 0, capitalLost: false, aidGiven: {}, aidTotal: 0, climateFinance: 0, fairShareOwed: 0,
    fairShareMetCount: 0, fairShareMeetings: 0, brokenPledges: 0, brokenTreaties: 0, energySoldToPartners: 0,
    innovationActs: 0, greenAbroad: 0, shortfallTurnsFood: 0, lastFoodShortfallTurn: -99, lastEnergyShortfallTurn: -99,
    foodNegativeEver: false, energyCoveredStreakFromT10: true, selfReliantStreak: 0, bestSelfReliantStreak: 0,
    refugeesAccepted: 0, refugeeResponseDone: false, brokerCount: 0, climateHitsSinceMeeting: 0,
    climateDamageSinceMeeting: 0, climateHitsTotal: 0, assemblyWins: 0, collapses: 0, bankrupt: false,
    importedThisTurn: false, pandemicResponded: false, financeWindow: 0, greenTransitions: 0, missionsRun: 0, spiesCaught: 0,
  };
}

/* ---------------------------------------------------------------------------------------------- */
/* Treaties, wars and relations                                                                     */
/* ---------------------------------------------------------------------------------------------- */

export function activeTreaties(s: WorldState, c: CountryId, type?: TreatyType): Treaty[] {
  return s.treaties.filter((t) => t.active && t.parties.includes(c) && (type === undefined || t.type === type));
}

export function hasTreaty(s: WorldState, a: CountryId, b: CountryId, type?: TreatyType): boolean {
  return s.treaties.some(
    (t) => t.active && t.parties.includes(a) && t.parties.includes(b) && (type === undefined || t.type === type),
  );
}

export function otherParty(t: Treaty, c: CountryId): CountryId {
  return t.parties[0] === c ? t.parties[1] : t.parties[0];
}

export function treatyPartners(s: WorldState, c: CountryId): CountryId[] {
  const out = new Set<CountryId>();
  for (const t of activeTreaties(s, c)) out.add(otherParty(t, c));
  return [...out];
}

export function atWar(s: WorldState, a: CountryId, b: CountryId): boolean {
  return s.wars.some((w) => w.active && ((w.a === a && w.b === b) || (w.a === b && w.b === a)));
}

export function enemiesOf(s: WorldState, c: CountryId): CountryId[] {
  const out: CountryId[] = [];
  for (const w of s.wars) {
    if (!w.active) continue;
    if (w.a === c) out.push(w.b);
    else if (w.b === c) out.push(w.a);
  }
  return out;
}

export function hasEmbassy(s: WorldState, a: CountryId, b: CountryId): boolean {
  return hasTreaty(s, a, b, 'embassy');
}

export function hasEffect(c: CountryState, kind: CountryState['effects'][number]['kind'], t: number): boolean {
  return c.effects.some((e) => e.kind === kind && e.until >= t);
}

export function tariffOn(s: WorldState, from: CountryId, to: CountryId, t: number): boolean {
  const c = s.countries[from];
  return !!c && c.tariffs.some((x) => x.target === to && x.until >= t);
}

/* ---------------------------------------------------------------------------------------------- */
/* Values                                                                                          */
/* ---------------------------------------------------------------------------------------------- */

export function indexCap(c: CountryState, key: IndexKey): number {
  if (key === 'wellbeing' && c.id === 'kestran') return 85;
  return LIMITS.index[1];
}

/**
 * Index gains bought with Treasury (programs, investments) have diminishing returns keyed to the
 * nation's overall National Index (so every country profile is treated alike): full below 60, half
 * from 60 to 74, a quarter from 75 (rounded up, at least 1). Returns the actual change.
 */
export function gain(c: CountryState, key: IndexKey, amount: number): number {
  if (amount <= 0) return bump(c, key, amount);
  return bump(c, key, gainAmount(c, amount));
}

/** National Index levels where bought gains drop to half and to a quarter. */
export const DR_FULL = 55;
export const DR_HALF = 65;

/** The index gain a purchase of `amount` gives this nation now (before the 0-100 clamp). */
export function gainAmount(c: CountryState, amount: number): number {
  if (amount <= 0) return amount;
  const v = (c.prosperity + c.security + c.wellbeing + c.environment) / 4;
  const f = v < DR_FULL ? 1 : v < DR_HALF ? 0.5 : 0.25;
  return Math.max(1, Math.ceil(amount * f));
}

/** Changes one index score with clamps. Returns the actual change. */
export function bump(c: CountryState, key: IndexKey, delta: number): number {
  if (delta === 0) return 0;
  const before = c[key];
  c[key] = clamp(before + delta, LIMITS.index[0], indexCap(c, key));
  return c[key] - before;
}

/** Changes Public Support with every ideology rule. Returns the actual change. */
export function changeSupport(c: CountryState, delta: number, opts: { climate?: boolean } = {}): number {
  if (delta === 0) return 0;
  let d = delta;
  // Ironvale: Constitutional Stability.
  if (d < 0 && c.id === 'ironvale') d = Math.max(d, -8);
  const before = c.support;
  let next = clamp(before + d, LIMITS.support[0], LIMITS.support[1]);
  if (d < 0) {
    // Aurem: Stability floor while Royal Grants are paid.
    if (c.id === 'aurem' && c.royalGrants && next < 40) next = Math.min(before, 40);
    // Atoll: climate can never push Public Support below 20.
    if (opts.climate && c.slot === 'atoll' && next < 20) next = Math.min(before, 20);
  }
  c.support = next;
  return next - before;
}

export function changeCohesion(c: CountryState, delta: number): number {
  const before = c.cohesion;
  c.cohesion = clamp(c.cohesion + delta, LIMITS.cohesion[0], LIMITS.cohesion[1]);
  return c.cohesion - before;
}

/** Adds (or removes) a stock, never below 0 or above the cap. Returns the actual change. */
export function addStock(c: CountryState, key: 'treasury' | 'energy' | 'food', delta: number): number {
  const before = c[key];
  c[key] = clamp(before + delta, 0, LIMITS.stock);
  return c[key] - before;
}

/** Changes Carbon and applies the symmetric Environment rule (E per point, Alvera E + 2). */
export function changeCarbon(c: CountryState, delta: number): number {
  const before = c.carbon;
  c.carbon = clamp(c.carbon + delta, LIMITS.carbon[0], LIMITS.carbon[1]);
  const actual = c.carbon - before;
  if (actual !== 0) {
    const per = 5 + (c.id === 'alvera' && actual < 0 ? CLIMATE_NUMBERS.alveraExtraPerCarbon : 0);
    bump(c, 'environment', -actual * per);
  }
  return actual;
}

export function supportBand(v: number): [number, number] {
  const lo = Math.min(90, Math.floor(v / 10) * 10);
  return [lo, lo + 10];
}

/** Par for a nation. Pass the world so a non-Classic pairing uses its own pars. */
export function parOf(c: CountryId, s?: Pick<WorldState, 'config'>): number {
  const table = s ? PARS_BY_PAIRING[s.config.pairing] ?? CLASSIC_PARS : CLASSIC_PARS;
  return table[c];
}

/** Displayed points during play: 50 + (Index - par). Used by the coalition-against-the-leader rule. */
export function displayedPoints(c: CountryState, s?: Pick<WorldState, 'config'>): number {
  return 50 + nationalIndex(c) - parOf(c.id, s);
}

export function leaders(s: WorldState): CountryId[] {
  const list = nations(s);
  if (list.length === 0) return [];
  const mean = list.reduce((a, c) => a + displayedPoints(c, s), 0) / list.length;
  return list.filter((c) => displayedPoints(c, s) >= mean + SCORING.leaderGap).map((c) => c.id);
}

/* ---------------------------------------------------------------------------------------------- */
/* Climate helpers                                                                                 */
/* ---------------------------------------------------------------------------------------------- */

export function stageFor(meter: number): HeatStage {
  const th = HEAT.stageThresholds;
  if (meter >= th[3]) return 4;
  if (meter >= th[2]) return 3;
  if (meter >= th[1]) return 2;
  return 1;
}

/** Integer-only meter increase: floor((total x 16 + n) / (2 n)); equals total exactly when n = 8. */
export function meterAdd(totalCarbon: number, n: number): number {
  return Math.floor((totalCarbon * 16 + n) / (2 * n));
}

export function stageRow(s: WorldState, c: CountryState): StageRow {
  return slotOf(c).climate[s.heat.stage];
}

/** Fair share per meeting = max(1, round(renormalized share x 2 x n_nations)). */
export function fairShare(s: WorldState, c: CountryState): number {
  const total = nations(s).reduce((a, x) => a + slotOf(x).share, 0);
  if (total === 0) return 1;
  const n = s.nations.length;
  return Math.max(1, Math.round((slotOf(c).share * 2 * n) / total));
}

/** Clean-tech tipping point threshold: world Carbon 6 below its Turn 0 total (scaled by n/8). */
export function cleanTechDrop(s: WorldState): number {
  return Math.max(1, Math.round((HEAT.cleanTechDrop8 * s.nations.length) / 8));
}

export function worldCarbon(s: WorldState): number {
  return nations(s).reduce((a, c) => a + c.carbon, 0);
}

/* ---------------------------------------------------------------------------------------------- */
/* Costs                                                                                           */
/* ---------------------------------------------------------------------------------------------- */

export function unitCost(c: CountryState, unit: UnitType, s: WorldState): number {
  let cost = UNIT_COST[unit];
  if (unit === 'army') {
    if (c.id === 'karsova') cost -= 1;
    cost -= slotOf(c).armyDiscount;
    // Meridia's Citizen Militia: Army recruitment costs 1 less while invaded.
    if (c.id === 'meridia' && ownZonesOccupied(s, c.id) > 0) cost -= 1;
  }
  return Math.max(1, cost);
}

export function programCost(c: CountryState, program: ProgramId, s?: WorldState): number {
  const p = PROGRAMS[program];
  let cost = p.cost;
  if (p.regional && (c.id === 'ironvale' || c.id === 'alvera')) cost -= 1;
  cost = Math.max(1, cost);
  return s ? Math.max(1, programCostHook(s, c, program, cost)) : cost;
}

/** Heat-Safe Work cost (1 under the Right to Stay Compact). */
export function heatSafeWorkCost(s: WorldState, base: number): number {
  return heatSafeCost(s, base);
}

export function greenCost(s: WorldState, c: CountryState): number {
  const base = slotOf(c).cheapGreen || c.id === 'brightwater' ? CLIMATE_NUMBERS.greenMinCost : 3;
  let cost = base;
  if (hasEffect(c, 'tech_discount', s.turn)) cost -= 1;
  if (s.heat.innovationPrizes > 0) cost -= 1;
  // Delta Pact: a pending Full Pact cut makes the switch 1 cheaper until its deadline.
  cost -= pactDiscount(s, c);
  // Discounts never stack below 2, except that the clean-tech tipping point and push lower the floor to 1.
  let floor: number = CLIMATE_NUMBERS.greenMinCost;
  if (s.heat.cleanTechTurn !== null) {
    cost -= 1;
    floor = CLIMATE_NUMBERS.greenFloorAfterTipping;
  }
  if (s.heat.cleanTechPush) {
    cost -= 1;
    floor = CLIMATE_NUMBERS.greenFloorAfterTipping;
  }
  return Math.max(floor, Math.min(base, cost));
}

export function resilienceCost(c: CountryState, level: number = c.resilience): number | null {
  if (level >= 3) return null;
  let cost: number = CLIMATE_NUMBERS.resilienceCosts[level] ?? 6;
  if (c.id === 'alvera' || c.id === 'karsova') cost -= 1;
  return Math.max(1, cost);
}

export function earlyWarningCost(c: CountryState): number {
  return c.id === 'karsova' ? 0 : 1;
}

export function treatyCost(s: WorldState, c: CountryState, partner: CountryId, transparency: boolean): number {
  let cost: number = UNIVERSAL.treatyCost;
  if (c.id === 'brightwater') cost -= 1;
  // Open port records under the Full Sea Patrol Accord end Kestran's first-treaty surcharge.
  if (c.id === 'kestran' && !c.treatyPartnersEver.includes(partner) && !transparency && passed(s, 'seas') !== 'full') cost += 1;
  return Math.max(0, cost);
}

/* ---------------------------------------------------------------------------------------------- */
/* Map                                                                                             */
/* ---------------------------------------------------------------------------------------------- */

export function zonesHeldBy(s: WorldState, c: CountryId): string[] {
  return s.zones.filter((z) => z.holder === c).map((z) => z.id);
}

export function foreignZonesHeld(s: WorldState, c: CountryId): number {
  return s.zones.filter((z) => z.holder === c && z.home !== null && z.home !== c).length;
}

export function ownZonesOccupied(s: WorldState, c: CountryId): number {
  return s.zones.filter((z) => z.home === c && z.holder !== c).length;
}

/* ---------------------------------------------------------------------------------------------- */
/* Economy formulas (used by resolution, previews and bots)                                        */
/* ---------------------------------------------------------------------------------------------- */

export interface Line {
  label: string;
  value: number;
}

function add(lines: Line[], label: string, value: number): void {
  if (value !== 0) lines.push({ label, value });
}

export function linesTotal(lines: Line[]): number {
  return lines.reduce((a, l) => a + l.value, 0);
}

export function growthDividend(s: WorldState, c: CountryState): number {
  if (c.id !== 'ironvale' || hasEffect(c, 'growth_paused', s.turn)) return 0;
  return clamp(Math.floor((c.prosperity - 50) / 10), 0, 3);
}

export function treasuryIncomeLines(s: WorldState, c: CountryState): Line[] {
  const lines: Line[] = [];
  const i = ideo(c);
  const sl = slotOf(c);
  const t = s.turn;
  add(lines, 'Standard income', FLOWS.treasuryIncome);
  add(lines, `${i.shortName} rules`, i.flows.treasury);
  add(lines, `${sl.name} land`, sl.flows.treasury);
  add(lines, 'Growth Dividend', growthDividend(s, c));
  if (c.id === 'alvera' && c.wellbeing >= 55) add(lines, 'Family Firms', 1);
  const trade = activeTreaties(s, c.id, 'trade').length;
  add(lines, 'Trade treaties', Math.min(trade, UNIVERSAL.tradeTreatyMaxCounted) * UNIVERSAL.tradeTreatyIncome);
  add(lines, 'Trade Hub', Math.min(trade, tradeHubCap(s, c)));
  add(lines, 'Free transit to the sea', freeTransit(s, c));
  let water = 0;
  let reloc = 0;
  let ftz = 0;
  for (const tr of activeTreaties(s, c.id)) {
    if (tr.type === 'water' && sl.upstream) water += 1;
    if (tr.type === 'relocation' && c.slot !== 'atoll') reloc += 1;
    if (tr.type === 'free_trade_zone') ftz += 1;
  }
  add(lines, 'Water Treaty', water);
  add(lines, 'Relocation Pact labor', reloc);
  add(lines, 'Free Trade Zone', ftz);
  add(lines, 'Occupied zones held', foreignZonesHeld(s, c.id) * UNIVERSAL.occupiedZoneTreasury);
  add(lines, 'Lost zones', -ownZonesOccupied(s, c.id) * UNIVERSAL.occupiedZoneTreasury);
  const autonomy = hasEffect(c, 'autonomy', t);
  if (!autonomy) {
    const embargoes = nations(s).filter((x) => x.id !== c.id && x.embargoes.includes(c.id)).length;
    const e = c.id === 'karsova' ? Math.floor(embargoes / 2) : embargoes;
    add(lines, 'Embargoes against you', e * UNIVERSAL.embargoIncome);
    if (hasEffect(c, 'sanctioned', t)) {
      add(lines, 'Assembly sanctions', c.id === 'karsova' ? UNIVERSAL.sanctionsIncome / 2 : UNIVERSAL.sanctionsIncome);
    }
  } else {
    add(lines, 'Strategic Autonomy', 2);
  }
  if (sl.solarExport) add(lines, 'Solar exports', Math.max(0, c.start.carbon - c.carbon));
  add(lines, 'Cleared forest farms', c.forestCleared * CLIMATE_NUMBERS.forestTreasury);
  add(lines, 'Arctic sea lane', laneIncome(s, c, stageRow(s, c)));
  return lines;
}

/** Unit upkeep: 25% of the cost of every unit above the Turn 0 force (the starting force is already budgeted). */
export function unitUpkeep(c: CountryState): number {
  let total = 0;
  for (const u of ['army', 'navy', 'air', 'defenses'] as const) total += Math.max(0, c.forces[u] - c.start.forces[u]) * UNIT_COST[u];
  return Math.floor((total * UNIT_UPKEEP_PERCENT) / 100);
}

export function programUpkeep(c: CountryState): number {
  let total = 0;
  for (const p of c.programs) total += PROGRAMS[p].cost;
  total += c.diversification * PROGRAMS.diversification.cost;
  return Math.floor((total * PROGRAM_UPKEEP_PERCENT) / 100);
}

export function agingUpkeep(s: WorldState, c: CountryState): number {
  if (c.id !== 'vellmark') return 0;
  if (c.programs.includes('workforce_renewal') || c.stats.refugeesAccepted >= 2) return 0;
  return (s.turn >= 8 ? 1 : 0) + (s.turn >= 14 ? 1 : 0);
}

export function treasuryUpkeepLines(s: WorldState, c: CountryState): Line[] {
  const lines: Line[] = [];
  const i = ideo(c);
  let units = unitUpkeep(c);
  if (c.id === 'ironvale' && hasEffect(c, 'umbrella', s.turn)) units = Math.max(0, units - 1);
  add(lines, 'Military upkeep', units);
  add(lines, 'Building upkeep', programUpkeep(c));
  if (i.flows.upkeep > 0) add(lines, `${i.shortName} programs`, i.flows.upkeep);
  if (i.flows.upkeep < 0) {
    // Meridia: upkeep 2 lower, never below 0.
    const before = linesTotal(lines);
    const cut = Math.min(before, -i.flows.upkeep);
    if (cut > 0) add(lines, 'Lean government', -cut);
  }
  add(lines, 'Aging population', agingUpkeep(s, c));
  add(lines, 'Permafrost repairs', permafrostUpkeep(s, stageRow(s, c)));
  if (c.posture === 'alert') add(lines, 'Alert posture', 1);
  if (c.id === 'aurem' && c.royalGrants) add(lines, 'Royal Grants', ROYAL_GRANTS);
  return lines;
}

export function treasuryIncome(s: WorldState, c: CountryState): number {
  return linesTotal(treasuryIncomeLines(s, c));
}

export function treasuryUpkeep(s: WorldState, c: CountryState): number {
  return Math.max(0, linesTotal(treasuryUpkeepLines(s, c)));
}

export function energyProductionLines(s: WorldState, c: CountryState): Line[] {
  const lines: Line[] = [];
  const i = ideo(c);
  const sl = slotOf(c);
  add(lines, 'Base production', FLOWS.energyBase);
  add(lines, 'Fossil power (Carbon)', c.carbon);
  add(lines, 'Clean power', c.cleanEnergy);
  add(lines, `${sl.name} land`, sl.flows.energy);
  add(lines, `${i.shortName} rules`, i.flows.energy);
  if (c.programs.includes('energy_grid')) add(lines, 'Clean Power Grid', PROGRAMS.energy_grid.flows.energy ?? 0);
  add(lines, 'Meltwater', stageRow(s, c).gain.energy ?? 0);
  if (c.islandDeveloped) add(lines, 'Island wells', 3);
  return lines;
}

export function energyUpkeepTotal(c: CountryState): number {
  return FLOWS.energyUpkeep + ideo(c).flows.energyUpkeep + slotOf(c).flows.energyUpkeep;
}

export function foodProductionLines(s: WorldState, c: CountryState): Line[] {
  const lines: Line[] = [];
  const i = ideo(c);
  const sl = slotOf(c);
  add(lines, 'Farms', FLOWS.foodBase);
  add(lines, `${sl.name} land`, sl.flows.food);
  add(lines, `${i.shortName} rules`, i.flows.food);
  if (c.programs.includes('farm_support')) add(lines, 'Farm Support', 1);
  add(lines, 'Cleared forest farms', c.forestCleared * CLIMATE_NUMBERS.forestFood);
  if (c.id === 'alvera' && c.wellbeing >= 55) add(lines, 'Family Firms', 1);
  add(lines, 'Northern farms', stageRow(s, c).gain.food ?? 0);
  add(lines, 'Irrigation (Water Fund)', irrigationFood(s, c));
  add(lines, 'Occupied zones held', foreignZonesHeld(s, c.id) * UNIVERSAL.occupiedZoneFood);
  add(lines, 'Lost zones', -ownZonesOccupied(s, c.id) * UNIVERSAL.occupiedZoneFood);
  return lines;
}

export function foodUpkeepTotal(_c: CountryState): number {
  return FLOWS.foodUpkeep;
}

export function energyNet(s: WorldState, c: CountryState): number {
  return linesTotal(energyProductionLines(s, c)) - energyUpkeepTotal(c);
}

export function foodNet(s: WorldState, c: CountryState): number {
  return linesTotal(foodProductionLines(s, c)) - foodUpkeepTotal(c);
}

export function treasuryNet(s: WorldState, c: CountryState): number {
  return treasuryIncome(s, c) - treasuryUpkeep(s, c);
}

/** World Market Energy price: Energy per 1 Treasury. */
export function sellRate(s: WorldState, c: CountryState): number {
  // The Just Transition bridge keeps the old price for nations that have started to change.
  if (oilBridge(s, c)) return 2;
  const low = tippingLowPrice(s) || (c.id === 'aurem' && s.turn >= 12 && c.diversification < 2);
  return low ? 3 : 2;
}

/** Forest Payments Carbon line (1, or 2 under the Just Transition Compact). */
export function forestLine(s: WorldState): number {
  return forestCarbonLine(s);
}

export function countryOf(s: WorldState, id: CountryId): CountryState {
  return getCountry(s, id);
}
